'use client';

import { c as _c } from "react/compiler-runtime";
import { InputGroup, InputGroupAddon, InputGroupInput } from '@repo/ui/components';
import { useState } from 'react';
import { useTimePicker } from './provider';
export const TimePickerInput = () => {
  const $ = _c(40);
  const {
    disabled,
    invalid,
    setValue,
    size,
    value
  } = useTimePicker();
  switch (value.type) {
    case "24":
    case "between":
      {
        let t0;
        if ($[0] !== setValue || $[1] !== value) {
          t0 = from_0 => setValue({
            ...value,
            from: from_0
          });
          $[0] = setValue;
          $[1] = value;
          $[2] = t0;
        } else {
          t0 = $[2];
        }
        let t1;
        if ($[3] !== disabled || $[4] !== invalid || $[5] !== size || $[6] !== t0 || $[7] !== value.from) {
          t1 = <TimeInput disabled={disabled} invalid={invalid} onChange={t0} size={size} value={value.from} />;
          $[3] = disabled;
          $[4] = invalid;
          $[5] = size;
          $[6] = t0;
          $[7] = value.from;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        let t2;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <span className="text-muted-foreground">-</span>;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        let t3;
        if ($[10] !== setValue || $[11] !== value) {
          t3 = to_0 => setValue({
            ...value,
            to: to_0
          });
          $[10] = setValue;
          $[11] = value;
          $[12] = t3;
        } else {
          t3 = $[12];
        }
        let t4;
        if ($[13] !== disabled || $[14] !== invalid || $[15] !== size || $[16] !== t3 || $[17] !== value.to) {
          t4 = <TimeInput disabled={disabled} invalid={invalid} onChange={t3} size={size} value={value.to} />;
          $[13] = disabled;
          $[14] = invalid;
          $[15] = size;
          $[16] = t3;
          $[17] = value.to;
          $[18] = t4;
        } else {
          t4 = $[18];
        }
        let t5;
        if ($[19] !== t1 || $[20] !== t4) {
          t5 = <>{t1}{t2}{t4}</>;
          $[19] = t1;
          $[20] = t4;
          $[21] = t5;
        } else {
          t5 = $[21];
        }
        return t5;
      }
    case "at":
      {
        let t0;
        if ($[22] !== setValue || $[23] !== value) {
          t0 = from => setValue({
            ...value,
            from
          });
          $[22] = setValue;
          $[23] = value;
          $[24] = t0;
        } else {
          t0 = $[24];
        }
        let t1;
        if ($[25] !== disabled || $[26] !== invalid || $[27] !== size || $[28] !== t0 || $[29] !== value.from) {
          t1 = <TimeInput disabled={disabled} invalid={invalid} onChange={t0} size={size} value={value.from} />;
          $[25] = disabled;
          $[26] = invalid;
          $[27] = size;
          $[28] = t0;
          $[29] = value.from;
          $[30] = t1;
        } else {
          t1 = $[30];
        }
        return t1;
      }
    case "by":
      {
        let t0;
        if ($[31] !== setValue || $[32] !== value) {
          t0 = to => setValue({
            ...value,
            to
          });
          $[31] = setValue;
          $[32] = value;
          $[33] = t0;
        } else {
          t0 = $[33];
        }
        let t1;
        if ($[34] !== disabled || $[35] !== invalid || $[36] !== size || $[37] !== t0 || $[38] !== value.to) {
          t1 = <TimeInput disabled={disabled} invalid={invalid} onChange={t0} size={size} value={value.to} />;
          $[34] = disabled;
          $[35] = invalid;
          $[36] = size;
          $[37] = t0;
          $[38] = value.to;
          $[39] = t1;
        } else {
          t1 = $[39];
        }
        return t1;
      }
    case "asap":
    case "tbc":
    default:
      {
        return null;
      }
  }
};
type TimeInputProps = {
  disabled?: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
  size?: 'default' | 'large';
  value?: null | string;
};
const parseTime = (timeStr?: null | string): {
  hour: string;
  minute: string;
} => {
  if (!timeStr) return {
    hour: '',
    minute: ''
  };
  const [hour, minute] = timeStr.split(':');
  return {
    hour: hour ?? '',
    minute: minute ?? ''
  };
};
const formatTime = (hour: string, minute: string): string | undefined => {
  if (!hour && !minute) return undefined;
  const h = hour.padStart(2, '0');
  const m = (minute || '0').padStart(2, '0');
  return `${h}:${m}`;
};
const clampValue = (value: string, max: number): string => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return '';
  if (num < 0) return '0';
  if (num > max) return String(max);
  return String(num);
};
const sanitizeInput = (value: string): string => value.replace(/\D/g, '').slice(0, 2);
const TimeInput = t0 => {
  const $ = _c(29);
  const {
    disabled: t1,
    invalid: t2,
    onChange,
    size: t3,
    value
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const invalid = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "default" : t3;
  let t4;
  if ($[0] !== value) {
    t4 = parseTime(value);
    $[0] = value;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    hour: parsedHour,
    minute: parsedMinute
  } = t4;
  const [localHour, setLocalHour] = useState(null);
  const [localMinute, setLocalMinute] = useState(null);
  const displayHour = localHour !== null ? localHour : parsedHour;
  const displayMinute = localMinute !== null ? localMinute : parsedMinute;
  let t5;
  if ($[2] !== displayMinute || $[3] !== onChange) {
    t5 = rawValue => {
      const sanitized = sanitizeInput(rawValue);
      setLocalHour(sanitized);
      if (!sanitized) {
        setLocalMinute(displayMinute);
      }
      onChange(sanitized ? formatTime(clampValue(sanitized, 23), displayMinute) ?? "" : "");
    };
    $[2] = displayMinute;
    $[3] = onChange;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleHourChange = t5;
  let t6;
  if ($[5] !== onChange || $[6] !== parsedHour) {
    t6 = rawValue_0 => {
      const sanitized_0 = sanitizeInput(rawValue_0);
      setLocalMinute(sanitized_0);
      if (sanitized_0) {
        const clamped = clampValue(sanitized_0, 59);
        const formatted = formatTime(parsedHour, clamped);
        onChange(formatted ?? "");
      } else {
        const formatted_0 = formatTime(parsedHour, "");
        onChange(formatted_0 ?? "");
      }
    };
    $[5] = onChange;
    $[6] = parsedHour;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleMinuteChange = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setLocalHour(null);
      setLocalMinute(null);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleHourBlur = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setLocalMinute(null);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleMinuteBlur = t8;
  const t9 = size === "large" ? "h-9 w-14" : "h-8 w-14";
  let t10;
  if ($[10] !== handleHourChange) {
    t10 = e => handleHourChange(e.target.value);
    $[10] = handleHourChange;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== disabled || $[13] !== displayHour || $[14] !== invalid || $[15] !== t10) {
    t11 = <InputGroupInput aria-invalid={invalid} className="px-0 py-0 text-center text-sm focus-visible:outline-none" disabled={disabled} inputMode="numeric" maxLength={2} onBlur={handleHourBlur} onChange={t10} onFocus={_temp} placeholder="HH" value={displayHour} data-sentry-element="InputGroupInput" data-sentry-source-file="input.tsx" />;
    $[12] = disabled;
    $[13] = displayHour;
    $[14] = invalid;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <InputGroupAddon align={null} data-sentry-element="InputGroupAddon" data-sentry-source-file="input.tsx">:</InputGroupAddon>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== handleMinuteChange) {
    t13 = e_1 => handleMinuteChange(e_1.target.value);
    $[18] = handleMinuteChange;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== disabled || $[21] !== displayMinute || $[22] !== invalid || $[23] !== t13) {
    t14 = <InputGroupInput aria-invalid={invalid} className="px-0 py-0 text-center text-sm focus-visible:outline-none" disabled={disabled} inputMode="numeric" maxLength={2} onBlur={handleMinuteBlur} onChange={t13} onFocus={_temp2} placeholder="MM" value={displayMinute} data-sentry-element="InputGroupInput" data-sentry-source-file="input.tsx" />;
    $[20] = disabled;
    $[21] = displayMinute;
    $[22] = invalid;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t11 || $[26] !== t14 || $[27] !== t9) {
    t15 = <InputGroup className={t9} data-sentry-element="InputGroup" data-sentry-component="TimeInput" data-sentry-source-file="input.tsx">{t11}{t12}{t14}</InputGroup>;
    $[25] = t11;
    $[26] = t14;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  return t15;
};
function _temp(e_0) {
  return e_0.target.select();
}
function _temp2(e_2) {
  return e_2.target.select();
}